import { css, html } from '@rxdi/lit-html';

/**
 * Shared building blocks for the studio design (home, navbar, footer).
 * Colours come only from `shared/themes/studio.css` variables.
 */
export const StudioStyles = css`
  :host {
    display: block;
    color: var(--rs-text);
    font-family: var(--rs-font-body);
    -webkit-font-smoothing: antialiased;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  .wrap {
    width: 100%;
    max-width: var(--rs-max);
    margin: 0 auto;
    padding: 0 var(--rs-gutter);
  }

  .section {
    position: relative;
    padding: clamp(64px, 8vw, 112px) 0;
  }

  .display,
  .h2 {
    text-wrap: balance;
  }

  .kicker {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-family: var(--rs-font-mono);
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--rs-text-muted);
  }

  .kicker::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--rs-signal);
    box-shadow: 0 0 12px var(--rs-signal-glow);
  }

  .kicker .index {
    color: var(--rs-signal);
  }

  .display {
    font-family: var(--rs-font-display);
    font-weight: 700;
    letter-spacing: -0.035em;
    line-height: 0.98;
    margin: 0;
  }

  .h2 {
    font-family: var(--rs-font-display);
    font-weight: 700;
    font-size: clamp(36px, 5.4vw, 72px);
    letter-spacing: -0.035em;
    line-height: 1;
    margin: 20px 0 0;
    max-width: 16ch;
  }

  .h2 em,
  .display em {
    font-style: normal;
    background: var(--rs-gradient-brand);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  .lead {
    font-size: clamp(17px, 1.6vw, 20px);
    line-height: 1.6;
    color: var(--rs-text-2);
    max-width: 60ch;
    margin: 24px 0 0;
  }

  .section-head {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    margin-bottom: clamp(48px, 7vw, 88px);
  }

  @media (min-width: 960px) {
    .section-head.split {
      grid-template-columns: 1.1fr 0.9fr;
      align-items: end;
    }
    .section-head.split .lead {
      margin: 0;
    }
  }

  /* buttons */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    height: 52px;
    padding: 0 26px;
    border-radius: 999px;
    font-family: var(--rs-font-body);
    font-weight: 600;
    font-size: 15px;
    cursor: pointer;
    border: 1px solid transparent;
    transition: transform 0.25s var(--rs-ease), box-shadow 0.25s var(--rs-ease), background 0.25s var(--rs-ease),
      border-color 0.25s var(--rs-ease);
    white-space: nowrap;
  }

  .btn svg {
    width: 16px;
    height: 16px;
    transition: transform 0.25s var(--rs-ease);
  }

  .btn:hover svg {
    transform: translateX(3px);
  }

  .btn-signal {
    background: var(--rs-signal);
    color: var(--rs-text-on-accent);
  }

  .btn-signal:hover {
    transform: translateY(-2px);
    box-shadow: var(--rs-shadow-signal);
  }

  .btn-ghost {
    background: transparent;
    color: var(--rs-text);
    border-color: var(--rs-line-strong);
  }

  .btn-ghost:hover {
    border-color: var(--rs-text-2);
    transform: translateY(-2px);
  }

  .tag {
    display: inline-flex;
    align-items: center;
    height: 28px;
    padding: 0 12px;
    border-radius: 999px;
    border: 1px solid var(--rs-line-strong);
    font-family: var(--rs-font-mono);
    font-size: 12px;
    color: var(--rs-text-2);
    white-space: nowrap;
  }

  .card {
    position: relative;
    background: var(--rs-gradient-card), var(--rs-surface);
    border: 1px solid var(--rs-line);
    border-radius: var(--rs-radius-lg);
  }

  .grid-bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image: linear-gradient(var(--rs-line) 1px, transparent 1px),
      linear-gradient(90deg, var(--rs-line) 1px, transparent 1px);
    background-size: 64px 64px;
    mask-image: radial-gradient(ellipse at center, var(--rs-ink) 0%, transparent 70%);
    -webkit-mask-image: radial-gradient(ellipse at center, var(--rs-ink) 0%, transparent 70%);
    opacity: 0.6;
  }

  /* loading placeholders */
  .skeleton {
    border-radius: 12px;
    background: linear-gradient(90deg, var(--rs-surface) 0%, var(--rs-surface-2) 50%, var(--rs-surface) 100%);
    background-size: 200% 100%;
    animation: shimmer 1.4s linear infinite;
  }

  .skeleton-line {
    width: var(--w, 100%);
    height: 16px;
    margin-top: 18px;
  }

  .skeleton-title {
    width: min(720px, 90%);
    height: clamp(56px, 8vw, 104px);
    margin-top: 22px;
  }

  .skeleton-card {
    min-height: 280px;
  }

  .skeleton-grid {
    display: grid;
    gap: 16px;
    margin-top: 48px;
  }

  @media (min-width: 900px) {
    .skeleton-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @keyframes shimmer {
    to {
      background-position: -200% 0;
    }
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.001ms !important;
    }
  }
`;

/** Arrow used inside buttons and links. */
export const ArrowIcon = html`<svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
  <path
    d="M3 8h10M9 4l4 4-4 4"
    stroke="currentColor"
    stroke-width="1.6"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;
